import React, { useState, useEffect } from 'react';
import ListingCard from './ListingCard';
import Icon from '../../../components/AppIcon';

const ListingsGrid = ({ listings, isLoading, onLoadMore, hasMore, onSave, isAuthenticated }) => {
  const [loadingMore, setLoadingMore] = useState(false);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setLoadingMore(false);
    }
  };

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement?.scrollTop
        >= document.documentElement?.offsetHeight - 1000
      ) {
        handleLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore]);

  if (isLoading && listings?.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)]?.map((_, index) => (
          <div key={index} className="bg-card rounded-lg shadow-organic animate-pulse">
            <div className="h-48 bg-muted rounded-t-lg" />
            <div className="p-4 space-y-3">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-3 bg-muted rounded w-1/2" />
              <div className="h-3 bg-muted rounded w-full" />
              <div className="h-3 bg-muted rounded w-2/3" />
              <div className="flex space-x-2">
                <div className="h-6 bg-muted rounded-full w-16" />
                <div className="h-6 bg-muted rounded-full w-20" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (listings?.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Search" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          No listings found
        </h3>
        <p className="text-muted-foreground font-body mb-6">
          Try adjusting your filters or search in a different area.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {listings?.map((listing) => (
          <ListingCard
            key={listing?.id}
            listing={listing}
            onSave={onSave}
            isAuthenticated={isAuthenticated}
          />
        ))}
      </div>
      {/* Loading More */}
      {loadingMore && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
          {[...Array(4)]?.map((_, index) => (
            <div key={index} className="bg-card rounded-lg shadow-organic animate-pulse">
              <div className="h-48 bg-muted rounded-t-lg" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
                <div className="h-3 bg-muted rounded w-full" />
                <div className="h-3 bg-muted rounded w-2/3" />
                <div className="flex space-x-2">
                  <div className="h-6 bg-muted rounded-full w-16" />
                  <div className="h-6 bg-muted rounded-full w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Load More Button (fallback for infinite scroll) */}
      {hasMore && !loadingMore && (
        <div className="text-center mt-8">
          <button
            onClick={handleLoadMore}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-organic font-body font-medium"
          >
            Load More Listings
          </button>
        </div>
      )}
      {/* End of Results */}
      {!hasMore && listings?.length > 0 && (
        <div className="text-center mt-8 py-6 border-t border-border">
          <p className="text-muted-foreground font-body">
            You've reached the end of the listings
          </p>
        </div>
      )}
    </div>
  );
};

export default ListingsGrid;